import axios from "axios";
import Cookies from "js-cookie";
import {withAuthCreds} from "./AuthCredentials";
import {onCallError} from "./Utils";

const API_URL = process.env.REACT_APP_API_URL;

// Authenticate and require the user to click a 2FA email to complete login
export async function loginWithMfa(username, password, otp) {
    try {
        return await axios.post(`${API_URL}/Account/LoginWithMfa`, {
            username: username,
            password: password,
            OTP: otp
        });
    } catch (error) {
        return onCallError(error)
    }
}

export async function CheckOtpEnabled(username, password) {
    try {
        return await axios.post(`${API_URL}/Account/CheckOtpEnabled`, {
            username: username,
            password: password,
        });
    } catch (error) {
        return onCallError(error)
    }
}

export async function SetupOtp(username, password) {
    try {
        return await axios.post(`${API_URL}/Account/SetupOtp`, {
            username: username,
            password: password,
        });
    } catch (error) {
        return onCallError(error)
    }
}

// Get Refresh Token
export async function refreshAccessToken(refreshToken) {
    try {
        return await axios.post(
            `${API_URL}/Account/RefreshToken`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                },
            }
        );
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * Registers a new user, and returns the responses.
 * Password is not required, as it is generated by the API,
 * and sent to the user's email.
 */
export async function registerUser(newUser) {
    try {
        return await withAuthCreds().put(`${API_URL}/Account/Register`, {
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            username: newUser.email,
            email: newUser.email,
            isLocked: newUser.isLocked,
            role: newUser.role,
            branches: newUser.branches
        });
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Sets the user's password after their account has been created.
 */
export async function setUserPassword(password, passwordToken, otp) {
    try {
        return await axios.patch(`${API_URL}/Account/SetPassword`, {
            password: password,
            OTP: otp
        }, {
            headers: {
                Authorization: `Bearer ${Cookies.get("resetToken")}`,
            },
        });
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * Updates a current user's information, and returns the responses.
 * @returns
 * @param user
 */
export async function updateUser(user) {
    try {
        return await withAuthCreds().patch(`${API_URL}/Account/ModifyUser`, {
            username: user.email,
            role: user.role,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            branches: user.branches
        });
    } catch (error) {
        return onCallError(error)
    }
}

export async function deleteUser(user) {
    try {
        return await withAuthCreds().delete(`${API_URL}/Account/DeleteUser?username=${user}`);
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Logs out current user on backend and deauthenticates tokens.
 */
export async function logoutUser() {
    try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
            const response = await withAuthCreds().post(
                `${API_URL}/Account/Logout`,
                {
                    shortTermAccessToken: Cookies.get("accessToken"),
                    longTermRefreshToken: refreshToken,
                    allowedSources: localStorage.getItem("allowedSources"),
                },
                {
                    headers: {
                        Authorization: `Bearer ${refreshToken}`,
                    },
                }
            );
            return response.data;
        }
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * getUserInfo - Gets a UserInfo object based on the username and sets the first and last name to local storage.
 * @param {string} username - The username of the logged in user.
 */
export async function getUserInfo(username) {
    try {
        return await withAuthCreds().get(`${API_URL}/Account/GetUserInfo?username=${username}`);
    } catch (error) {
        return onCallError(error)
    }
}

export async function getAllUsers() {
    try {
        const response = await withAuthCreds().get(
            `${API_URL}/Account/GetAllUsers`
        );
        return response.data;
    } catch (error) {
        return onCallError(error)
    }
}

export async function getAvailableBranches() {
    try {
        const response = await withAuthCreds().get(
            `${API_URL}/Account/GetAvailableBranches`
        );
        return response.data;
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Adds a new bank branch to the API.
 * @param branchName - A simple string name for the new branch.
 * @param timeZone - A string value for the new time zone
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function addBankBranch(branchName, timeZone) {
    try {
        return await withAuthCreds().post(
            `${API_URL}/Account/AddBankBranch?branchName=${branchName}&timeZone=${timeZone}`
        );
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description - Removes a bank branch from the API based on the branch id.
 * @param branchId - The string form of the branch guid to remove
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function removeBankBranch(branchId) {
    try {
        return await withAuthCreds().delete(
            `${API_URL}/Account/RemoveBankBranch?branchId=${branchId}`
        );
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description - Gets menu items for TimeZone options.
 * @returns {Promise<axios.AxiosResponse<any>>}
 */
export async function getTimeZones() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Account/GetBranchTimeZones`
        );
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Adds a new terminal to a specified branch.
 * @param {string} branchId - The guid string of the branch to which the terminal will be added.
 * @param {string} terminalName - The name of the terminal to be added.
 * @returns {Promise<axios.AxiosResponse<any>>} - The response from the API.
 */
export async function addNewTerminalToBranch(branchId, terminalName) {
    try {
        return await withAuthCreds().post(
            `${API_URL}/Account/AddNewTerminalToBranch?branchId=${branchId}&terminalName=${terminalName}`
        );
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * getReviews - Gets all reviews from the API, based on the state filter.
 * @param {string} stateFilter - The state of the reviews to get.
 * @param startDate - The start date of the reviews to get.
 * @param endDate - The end date of the reviews to get.
 * @returns {Array} - An array of reviews.
 * @example getReviews("Pending")
 * @example getReviews("Approved")
 * @example getReviews("Rejected")
 */
export async function getReviews(stateFilter, startDate, endDate) {
    try {
        // Min/Max dates set to assist proper SQL date range queries
        const minDate = new Date(1753, 1, 2);
        const maxDate = new Date(9999, 12, 31);

        startDate = startDate || minDate;
        endDate = endDate || maxDate;

        // Format dates to utilize day, month, and year without timezone info
        startDate = startDate < minDate ? minDate : startDate;
        endDate = endDate > maxDate ? maxDate : endDate;

        const response = await withAuthCreds().get(
            `${API_URL}/Review/GetReviews?state=${stateFilter}&startOfRange=${startDate}&endOfRange=${endDate}`
        );
        return response.data;
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * getDashData - Gets Dashboard data, based on the date range.
 * @param {string} stateFilter - The state of the reviews to get.
 * @param startDate - The start date of the reviews to get.
 * @param endDate - The end date of the reviews to get.
 * @returns {Array} - An array of reviews.
 * @example getDashData("Pending", new Date().Now, new Date().setMonth(Date().getMonth - 3)
 * @example getDashData("Approved", new Date().Now, new Date().setMonth(Date().getMonth - 3)
 * @example getDashData("Rejected", new Date().Now, new Date().setMonth(Date().getMonth - 3)
 */
export async function getDashData(stateFilter, startDate, endDate) {
    try {
        // Min/Max dates set to assist proper SQL date range queries
        const minDate = new Date(1753, 1, 2);
        const maxDate = new Date(9999, 12, 31);

        startDate = startDate || minDate;
        endDate = endDate || maxDate;

        // Format dates to utilize day, month, and year without timezone info
        startDate = startDate < minDate ? minDate : startDate;
        endDate = endDate > maxDate ? maxDate : endDate;

        const response = await withAuthCreds().get(
            `${API_URL}/Dashboard/GetDashboards?state=${stateFilter}&startOfRange=${startDate}&endOfRange=${endDate}`
        );
        return response.data;
    } catch (error) {
        return onCallError(error)
    }
}

export async function getCustomerConfig() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Config/GetCustomerConfig`
        );
    } catch (error) {
        return onCallError(error)
    }
}

export async function userResetPassword(username) {
    try {
        return await axios.post(`${API_URL}/Account/UserResetPassword?username=${username}`
        );
    } catch (error) {
        return onCallError(error)
    }
}

export async function adminResetPassword(username) {
    try {
        return await withAuthCreds().post(`${API_URL}/Account/AdminResetPassword?username=${username}`);
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Gets check images from the API, based on the transaction id and item id.
 * @param tranId - Transaction id of the transaction to get.
 * @param itemId - Id of specific check item to get.
 * @returns {Object} - An object with the front and back check images.
 */
export async function getCheckImages(tranId, itemId) {
    try {
        let response = await withAuthCreds().get(
            `${API_URL}/Review/GetCheckImages?tranId=${tranId}&itemId=${itemId}`
        );
        return response.data;
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Gets a list of all blacklisted accounts from the API.
 */
export async function getBlacklistDestAccounts() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Config/GetBlacklistDestAccounts`
        )
    } catch (error) {
        return onCallError(error)
    }
}

/**
 * @description Gets a list of all blacklisted routing numbers from the API.
 */
export async function getBlacklistedRoutings() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Config/GetBlacklistedRoutings`
        )
    } catch (error) {
        return onCallError(error)
    }
}

export async function getBranchTerminalIds() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Account/GetTerminalsByBank`
        )
    } catch (error) {
        return onCallError(error)
    }
}

export async function getTerminalIdsByBranch(branchId) {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Account/GetTerminalsByBranch?branchId=${branchId}`
        )
    } catch (error) {
        return onCallError(error)
    }
}


/**
 * @description Updates the terminal IDs associated with a specific branch.
 * @param {string} branchId - The guid string of the branch to update.
 * @param {string} terminalId - The guid string of the terminal to associate with the branch.
 * @returns {Promise<axios.AxiosResponse<any>>} - The response from the API.
 */
export async function updateTerminalIdsByBranch(branchId, terminalId) {
    try {
        return await withAuthCreds().post(
            `${API_URL}/Account/UpdateTerminalIdsByBranch?branchId=${branchId}&terminalId=${terminalId}`
        );
    } catch (error) {
        return onCallError(error)
    }
}

export async function getCheckPreviewData() {
    try {
        return await withAuthCreds().get(
            `${API_URL}/Config/GetCheckPreviewData`
        )
    } catch (error) {
        return onCallError(error)
    }
}